import React, { useEffect, useRef, useState } from 'react';
import { Zap, AlertTriangle } from 'lucide-react';
import { eventBus } from '../core/EventBus';
import { p2pAssistService } from '../core/P2PAssistService';

interface DialogState {
  visible: boolean;
  stunServers: string[];
  rttCurrentMs?: number;
  rttP2PMs?: number;
  countdownSec: number;
  remainingSec: number;
}

const initialState: DialogState = {
  visible: false,
  stunServers: [],
  countdownSec: 10,
  remainingSec: 10,
};

const P2PAssistDialog: React.FC = () => {
  const [state, setState] = useState<DialogState>(initialState);
  const tickTimerRef = useRef<number | null>(null);
  const stateRef = useRef<DialogState>(initialState);
  stateRef.current = state;

  // 清理倒计时
  const clearTick = () => {
    if (tickTimerRef.current !== null) {
      window.clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
  };

  // 关闭弹窗
  const closeDialog = () => {
    clearTick();
    setState(initialState);
  };

  // 用户点击立即切换 / 倒计时到时
  const handleAccept = () => {
    closeDialog();
    p2pAssistService.acceptSwitch();
  };

  // 用户点击取消
  const handleReject = () => {
    closeDialog();
    p2pAssistService.rejectSwitch();
  };

  useEffect(() => {
    const handleShow = (data: {
      sessionId: string;
      stunServers: string[];
      rttCurrentMs?: number;
      rttP2PMs?: number;
      countdownSec: number;
    }) => {
      clearTick();
      setState({
        visible: true,
        stunServers: data.stunServers || [],
        rttCurrentMs: data.rttCurrentMs,
        rttP2PMs: data.rttP2PMs,
        countdownSec: data.countdownSec || 10,
        remainingSec: data.countdownSec || 10,
      });

      // 启动倒计时（每秒一次）
      tickTimerRef.current = window.setInterval(() => {
        setState((prev) => {
          if (!prev.visible) return prev;
          const next = prev.remainingSec - 1;
          if (next <= 0) {
            // 倒计时到，自动切换
            clearTick();
            // 用 setTimeout 跳出 setState 回调再调用，避免在 render 阶段触发副作用
            window.setTimeout(() => {
              if (stateRef.current.visible) {
                handleAccept();
              }
            }, 0);
            return { ...prev, remainingSec: 0 };
          }
          return { ...prev, remainingSec: next };
        });
      }, 1000);
    };

    // 主连接断开时强制关闭弹窗（避免残留）
    const handleConnectionStateChange = (data: { state: string }) => {
      if (
        stateRef.current.visible &&
        (data.state === 'disconnected' || data.state === 'failed' || data.state === 'closed')
      ) {
        closeDialog();
      }
    };

    eventBus.on('ui:showP2PAssistDialog', handleShow);
    eventBus.on('connection:stateChange', handleConnectionStateChange);

    return () => {
      eventBus.off('ui:showP2PAssistDialog', handleShow);
      eventBus.off('connection:stateChange', handleConnectionStateChange);
      clearTick();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!state.visible) return null;

  const progressPercent = Math.max(0, Math.min(100, (state.remainingSec / state.countdownSec) * 100));
  const showRttCompare = typeof state.rttCurrentMs === 'number' || typeof state.rttP2PMs === 'number';

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-gray-900/95 rounded-2xl shadow-2xl border border-emerald-500/40 max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-emerald-900/30 px-6 py-4 flex items-center gap-3 border-b border-emerald-500/30">
          <Zap className="w-7 h-7 text-emerald-400" />
          <div className="flex-1">
            <h2 className="text-lg font-bold text-emerald-300">发现更优 P2P 直连线路</h2>
            <p className="text-xs text-emerald-200/70">已通过质量测试，可切换为直连降低延迟</p>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* RTT 对比（如果有数据） */}
          {showRttCompare && (
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-800/60 rounded-lg p-3 border border-gray-700/40">
                <div className="text-xs text-gray-400 mb-1">当前：中转</div>
                <div className="text-lg font-semibold text-gray-200">
                  {typeof state.rttCurrentMs === 'number' ? `${Math.round(state.rttCurrentMs)} ms` : '-- ms'}
                </div>
              </div>
              <div className="bg-emerald-900/30 rounded-lg p-3 border border-emerald-700/40">
                <div className="text-xs text-emerald-300/80 mb-1">P2P 直连</div>
                <div className="text-lg font-semibold text-emerald-200">
                  {typeof state.rttP2PMs === 'number' ? `${Math.round(state.rttP2PMs)} ms` : '-- ms'}
                </div>
              </div>
            </div>
          )}

          {/* 倒计时 + 进度条 */}
          <div>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-gray-300">将在 {state.remainingSec} 秒后自动切换</span>
            </div>
            <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-[width] duration-1000 ease-linear"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* 卡顿警告 */}
          <div className="flex items-start gap-2 bg-amber-900/20 border border-amber-700/40 rounded-lg p-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <span className="text-xs text-amber-200/90">切换瞬间画面会短暂卡顿一下</span>
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 py-4 bg-gray-800/30 flex justify-end gap-3 border-t border-gray-700/50">
          <button
            onClick={handleReject}
            className="px-5 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg font-medium transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleAccept}
            autoFocus
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-medium transition-colors shadow-lg"
          >
            立即切换
          </button>
        </div>
      </div>
    </div>
  );
};

export default P2PAssistDialog;
